import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "~/atoms/button";
import { twoFactorAuth } from "../api/apiRequests";
import { FetchError } from "../api/types";
import { CenterSpinner } from "../atoms/spinner";
import { type TQRcode } from "../config/types";
import { useUserContext } from "../hooks/useUserContext";
import { enableTwoFa } from "../api/v2/requests";
import { useNavigate } from "react-router";
import { t } from "i18next";
import { AccountTab } from "./AccountSettings";
import { Input } from "~/atoms/input";
import { Label } from "~/atoms/label";

const QRCodeSection = ({ otpauthUrl }: { otpauthUrl: string }) => (
  <div className="flex flex-col gap-2">
    <p className="text-sm text-gray-500">
      {t(($) => $.settings["2fa"].qrCodeDesc)}
    </p>
    <div className="flex justify-center">
      <QRCodeCanvas value={otpauthUrl} />
    </div>
  </div>
);

const copyToClipBoard = async (copyText: string) => {
  try {
    await navigator.clipboard.writeText(copyText);
    toast.success(t(($) => $.toasts.success.keyCopied));
  } catch {
    toast.error(t(($) => $.toasts.error.generic));
  }
};

const SecretKeySection = ({ secret }: { secret: string }) => (
  <div className="flex flex-col gap-2">
    <p className="text-sm text-gray-500">
      {t(($) => $.settings["2fa"].orCopy)}
    </p>
    <div className="flex gap-2">
      <Input value={secret} readOnly />
      <Button onClick={() => copyToClipBoard(secret)} variant="outline">
        {t(($) => $.settings["2fa"].copy)}
      </Button>
    </div>
  </div>
);

const VerificationForm = ({
  authCode,
  setAuthCode,
  onSubmit,
  isDisabled,
}: {
  authCode: string;
  setAuthCode: (value: string) => void;
  onSubmit: () => void;
  isDisabled: boolean;
}) => (
  <div className="space-y-2">
    <p className="text-sm text-gray-500">
      {t(($) => $.settings["2fa"].verificationDesc)}
    </p>
    <form onSubmit={onSubmit} className="space-y-2">
      <Label className="text-sm">
        {t(($) => $.settings["2fa"].characterCode)}
      </Label>
      <Input value={authCode} onChange={(e) => setAuthCode(e.target.value)} />
      <Button type="submit" disabled={isDisabled}>
        {t(($) => $.commons.buttons.submit)}
      </Button>
    </form>
  </div>
);

const TwoFactorAuthPage = () => {
  const { user, setUser } = useUserContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [newQRcode, setNewQRcode] = useState<TQRcode | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [authCode, setAuthCode] = useState("");

  const disabledAuthSubmit = () => {
    if (authCode.length !== 6) return false;

    return /^\d+$/.test(authCode);
  };

  const submitAuth = async () => {
    if (!newQRcode) return;

    setIsLoading(true);
    try {
      await enableTwoFa(authCode, newQRcode.secret);
      setUser({ ...user, isTwoFaEnabled: true });
      toast.success(t(($) => $.toasts.success["2faActivated"]));
      await navigate(`/account-settings?tab=${AccountTab.twoFA}`);
    } catch (err) {
      if (err instanceof FetchError && err.code === "INVALID_CREDENTIALS") {
        toast.error(t(($) => $.toasts.error.invalid2faCode));
      } else {
        toast.error(t(($) => $.toasts.error.generic));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user.isTwoFaEnabled) {
      void navigate("/account-settings");
      return;
    }

    setIsLoading(true);
    twoFactorAuth
      .generateQRcode()
      .then((qrCode) => setNewQRcode(qrCode as TQRcode))
      .catch(() => navigate("/account-settings"))
      .finally(() => setIsLoading(false));
  }, [user, navigate]);

  if (isLoading) {
    return <CenterSpinner />;
  }

  if (!newQRcode) {
    void navigate("/account-settings");
    return null;
  }

  return (
    <div className="grid place-items-center pt-[10%]">
      <div className="flex max-w-xl flex-col gap-4 rounded-md bg-white p-8">
        <h3 className="text-lg font-medium">
          {t(($) => $.settings["2fa"].setUpLong)}
        </h3>
        <QRCodeSection otpauthUrl={newQRcode.otpauthUrl} />
        <SecretKeySection secret={newQRcode.secret} />
        <VerificationForm
          authCode={authCode}
          setAuthCode={setAuthCode}
          onSubmit={submitAuth}
          isDisabled={!disabledAuthSubmit()}
        />
      </div>
    </div>
  );
};

export default TwoFactorAuthPage;
